import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import analyticsService from '../../services/analyticsService';
import EmptyState from './EmptyState';
import type { AnalyticsFilters, ByTimeResponse } from '../../types/analytics';

interface Props {
  filters: AnalyticsFilters;
  dimension: 'dow' | 'hour' | 'dte';
  height?: number;
}

const TimeSliceChart: React.FC<Props> = ({ filters, dimension, height = 220 }) => {
  const [data, setData] = useState<ByTimeResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    analyticsService.getByTime(filters, dimension)
      .then(r => { if (!cancelled) setData(r); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, dimension]);

  const points = useMemo(() => (data?.items || []).map(it => ({
    label: dimension === 'hour' ? `${it.key}:00` : it.key,
    pnl: it.pnl,
    trades: it.trades,
  })), [data, dimension]);

  if (loading) return <div className="py-6 text-center text-sm text-gray-400">Loading…</div>;
  if (!points.length) return <EmptyState />;

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.18} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={70}
                 tickFormatter={(v) => `₹${Math.round(v).toLocaleString('en-IN')}`} />
          <Tooltip
            formatter={((v: any) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`) as any}
            contentStyle={{ borderRadius: 6, fontSize: 12 }}
          />
          <Bar dataKey="pnl" name="P&L">
            {points.map((p, i) => (
              <Cell key={i} fill={p.pnl >= 0 ? '#10b981' : '#ef4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeSliceChart;
